import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import {
  ensureUniquenessAfterAddOrChangeToListData,
  propagateAfterAddsChangesOrDeletes,
} from '../../modules/mockDataCommon'
import { _storeEntityDataListToLocalForage } from '../../modules/mockDataStorageManager'
import {
  getOpportunityListData,
  getOpportunityOverviewData,
} from '../opportunities/mockData'
import {
  _getTMDataById,
  getTeamMemberListData,
} from '~/api/msw/api/teamMembers/mockData'
import type { TEntityId } from '~/types/models'
import type { IOpportunityTeamMember } from '@/types/opportunity/models'
import { propagateOpportunityTeamMemberData } from '~/cynqueModules/dataObjects/OpportunityTeamMember'

const mn = '[api/msw/api/opportunityTeamMmebers/mockData.ts]'

const opportunityTeamMemberListData =
  undefined as unknown as IOpportunityTeamMember[]

export const getOpportunityTeamMemberListData = getListDataFunctionFactory<
  IOpportunityTeamMember[]
>({
  listData: opportunityTeamMemberListData,
  storageKey: 'opportunityteammembers',
  getStartingListData: getStartingOportunityTeamMemberListData,
})

export async function getOpportunityTeamMemberDataById(id: string) {
  const list = await getOpportunityTeamMemberListData()
  if (!list) {
    throw new Error(`getOpportunityTeamMemberListData returned undefined/null`)
  }
  return list.find((otm: IOpportunityTeamMember) => otm.id === id)
}

export async function getOpportunityTeamMemberListDataByOpportunityId(
  id: TEntityId
): Promise<IOpportunityTeamMember[]> {
  const list: IOpportunityTeamMember[] =
    await getOpportunityTeamMemberListData()
  if (!list) {
    throw new Error(`getOpportunityTeamMemberListData returned undefined/null`)
  }
  const calls = list.filter((otm) => otm.opportunity.id === id) ?? []
  return calls
}

//
// handlers
//

export async function processAfterAddOrChangeToOpportunityTeamMemberListData({
  targetItem,
  updatedEntityData,
  listData,
}: {
  targetItem: IOpportunityTeamMember
  updatedEntityData: Partial<IOpportunityTeamMember>
  listData: IOpportunityTeamMember[]
}) {
  const fn = `${mn} processAfterAddOrChangeToOpportunityTeamMemberListData |`
  console.debug(`${fn} called`)
  await ensureUniquenessAfterAddOrChangeToListData<IOpportunityTeamMember>({
    targetItem,
    updatedEntityData,
    listData,
    propertyNameArray: ['isOwner', 'isAssignedTo'],
    entityKeyBase: 'opportunityteammembers',
    getListDataFn: getOpportunityTeamMemberListData,
    filterFn: (otm: IOpportunityTeamMember) =>
      otm.opportunity.id === targetItem.opportunity.id,
  })
  await propagateAfterAddsChangesOrDeletes<IOpportunityTeamMember>({
    sourceItem: targetItem,
    sourceEntityName: 'opportunityTeamMember',
    targetEntityInSourceItem: 'opportunity',
    getOverviewFn: getOpportunityOverviewData,
    propagationFn: propagateOpportunityTeamMemberData,
    getTargetEntityListDataFn: getOpportunityListData,
    entityKeyBase: 'opportunities',
  })
}

export async function processAfterDeleteOfOpportunityTeamMemberListData({
  deletedEntityData,
}: {
  deletedEntityData: IOpportunityTeamMember
}) {
  const fn = `${mn} processAfterDeleteOfOpportunityTeamMemberListData |`
  console.debug(`${fn} called...`)
  await propagateAfterAddsChangesOrDeletes<IOpportunityTeamMember>({
    sourceItem: deletedEntityData,
    sourceEntityName: 'opportunityTeamMember',
    targetEntityInSourceItem: 'opportunity',
    getOverviewFn: getOpportunityOverviewData,
    propagationFn: propagateOpportunityTeamMemberData,
    getTargetEntityListDataFn: getOpportunityListData,
    entityKeyBase: 'opportunities',
  })
}

//
// Get starting listData
//

export async function getStartingOportunityTeamMemberListData(): Promise<
  IOpportunityTeamMember[]
> {
  const tmList = await getTeamMemberListData()
  const o0003 = { id: 'XXO0003', label: 'DIA - All Locations' }
  return [
    {
      id: 'XXOTM0001',
      hrid: 'OTM0001',
      opportunity: o0003,
      teamMember: _getTMDataById('XXTM0001', tmList)!,
      splitFraction: 0.1,
      isOwner: true,
      isAssignedTo: true,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXOTM0005',
      hrid: 'OTM0005',
      opportunity: o0003,
      teamMember: _getTMDataById('XXTM0005', tmList)!,
      splitFraction: 0.05,
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
  ]
}
